import React, { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import {
    Camera,
    Film,
    Image as ImageIcon,
    Palette,
    Package,
    ShoppingBag,
    Lightbulb,
    Layers,
    Tag,
    Box,
    Sparkles,
    User
} from 'lucide-react';

const productServices = [
    { id: '01', icon: Package, title: 'E-Commerce White-Background', desc: 'Sleek, shadow-controlled, and high-definition product captures optimized for Amazon, Shopify, and global marketplaces.' },
    { id: '02', icon: Box, title: 'Creative Flat Lays', desc: 'Artistically arranged overhead compositions highlighting product collections, color variants, and dimensions.' },
    { id: '03', icon: Layers, title: 'Macro Texture & Detail Sets', desc: 'Ultra-magnified detail captures showcasing premium materials, cosmetic swatches, and intricate fabric weaves.' },
    { id: '04', icon: Lightbulb, title: 'Creative Lifestyle Staging', desc: 'Placing your products in natural, curated contextual environments that evoke aspirational consumer lifestyles.' },
    { id: '05', icon: ShoppingBag, title: 'Packaging & Unboxing Media', desc: 'Capturing your premium exterior shipping boxes, custom mailers, and unboxing aesthetic details.' },
    { id: '06', icon: Film, title: 'Product Motion & 360° Reels', desc: 'Dynamic short-form video assets emphasizing texture reflection, functionality, and multi-dimensional scale.' },
];

const brandServices = [
    { id: '01', icon: Tag, title: 'Visual Identity Campaigns', desc: 'Conceptual visual assets built specifically to establish and drive your brand identity across all media.' },
    { id: '02', icon: Film, title: 'Brand Narrative Short Films', desc: 'Cinematic mini-documentaries showcasing your origins, manufacturing process, and company ethos.' },
    { id: '03', icon: User, title: 'Founder & Executive Portraits', desc: 'Polished editorial portrait sessions celebrating the visionary minds driving the enterprise.' },
    { id: '04', icon: Sparkles, title: 'Social Media Core Content Kits', desc: 'High-volume aesthetic content calendars tailored perfectly to drive viral Instagram and TikTok loops.' },
    { id: '05', icon: ImageIcon, title: 'Brand Re-Launch Portfolios', desc: 'Comprehensive premium visual overhauls structured to align with updated modern design guidelines.' },
];

const comprehensiveBrandOfferings = [
    { id: '01', icon: Camera, title: 'Studio Lighting Architecture', desc: 'Custom studio strobe profiles calculated to eliminate harsh glare on metallic and glass surfaces.' },
    { id: '02', icon: Palette, title: 'Pantone Color Calibration', desc: 'Strict display-to-print color matching ensuring your on-screen image reflects the true-to-life product dye.' },
    { id: '03', icon: Layers, title: 'Transparent Background Cutting', desc: 'Hand-clipped pixel-perfect clipping paths and alpha-channel extractions for infinite graphic layouts.' },
    { id: '04', icon: Box, title: 'Ghost Mannequin Modeling', desc: 'Premium apparel presentation using modular forms to reveal inner lining details and dimensional volume.' },
    { id: '05', icon: ImageIcon, title: 'High-End Product Retouching', desc: 'Meticulous dust, scratch, and mold-line removal resulting in completely immaculate consumer assets.' },
    { id: '06', icon: Tag, title: 'Cross-Platform Formatting', desc: 'Delivering tailored dimension aspect ratios ideal for banners, billboards, print ads, and social media formats.' },
    { id: '07', icon: Film, title: 'BTS Concept Featurettes', desc: 'Engaging behind-the-scenes visual captures highlighting the raw craft, set building, and styling phase.' },
    { id: '08', icon: Lightbulb, title: 'Visual Prop Curation & Assembly', desc: 'Sourcing contextual materials, backdrops, and complimentary surfaces that amplify your core product story.' },
    { id: '09', icon: ImageIcon, title: 'Encrypted Media Asset Delivery', desc: 'High-speed cloud delivery networks enabling rapid asset selection, organization, and download access.' },
];

const ProductsandBrands = () => {
    const viewportRef = useRef(null);
    const leftStripRef = useRef(null);
    const rightStripRef = useRef(null);

    useEffect(() => {
        const viewport = viewportRef.current;
        const leftStrip = leftStripRef.current;
        const rightStrip = rightStripRef.current;

        if (!viewport || !leftStrip || !rightStrip) return;

        let currentPosLeft = 0;
        let currentPosRight = 0;
        let isHovered = false;
        let animationFrameId;

        const speedLeft = 0.6;
        const speedRight = 0.4;

        const handleMouseEnter = () => { isHovered = true; };
        const handleMouseLeave = () => { isHovered = false; };

        viewport.addEventListener('mouseenter', handleMouseEnter);
        viewport.addEventListener('mouseleave', handleMouseLeave);

        function performAutoScroll() {
            if (!isHovered && leftStrip && rightStrip) {
                currentPosLeft -= speedLeft;
                currentPosRight -= speedRight;

                if (Math.abs(currentPosLeft) >= (leftStrip.scrollHeight / 2)) {
                    currentPosLeft = 0;
                }
                if (Math.abs(currentPosRight) >= (rightStrip.scrollHeight / 2)) {
                    currentPosRight = 0;
                }

                if (window.innerWidth > 991) {
                    leftStrip.style.transform = `translateY(${currentPosLeft}px)`;
                    rightStrip.style.transform = `translateY(${currentPosRight}px)`;
                } else {
                    leftStrip.style.transform = 'none';
                    rightStrip.style.transform = 'none';
                }
            }
            animationFrameId = requestAnimationFrame(performAutoScroll);
        }

        animationFrameId = requestAnimationFrame(performAutoScroll);

        return () => {
            cancelAnimationFrame(animationFrameId);
            if (viewport) {
                viewport.removeEventListener('mouseenter', handleMouseEnter);
                viewport.removeEventListener('mouseleave', handleMouseLeave);
            }
        };
    }, []);

    return (
        <div>
            <Helmet>
                <title>Product & Brand Photography in Hyderabad | KhultStudios</title>

                <meta
                    name="description"
                    content="Professional Product & Brand Photography in Hyderabad by KhultStudios. We create high-quality eCommerce product photos, Amazon & Flipkart listing images, lifestyle photography, advertising campaigns, catalog photography, and brand content."
                />

                <meta
                    name="keywords"
                    content="Product Photography Hyderabad, Brand Photography Hyderabad, eCommerce Photography, Amazon Product Photography, Flipkart Product Photography, Catalog Photography, Lifestyle Product Photography, Commercial Product Photography, Advertising Photography, Food Product Photography, Cosmetic Product Photography, Jewellery Photography, Apparel Product Photography, Product Videography Hyderabad, Brand Campaign Photography, KhultStudios"
                />

                <meta name="robots" content="index, follow" />
                <meta name="author" content="KhultStudios" />

                <link
                    rel="canonical"
                    href="https://khultstudios.com/products-and-brands-photography"
                />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta
                    property="og:url"
                    content="https://khultstudios.com/products-and-brands-photography"
                />
                <meta
                    property="og:title"
                    content="Product & Brand Photography in Hyderabad | KhultStudios"
                />
                <meta
                    property="og:description"
                    content="Professional product photography for eCommerce, Amazon, Flipkart, websites, catalogs, advertising, and branding. Elevate your brand with premium visuals by KhultStudios."
                />
                <meta
                    property="og:image"
                    content="https://khultstudios.com/images/product-brand-photography-banner.jpg"
                />
                <meta property="og:site_name" content="KhultStudios" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content="Product & Brand Photography | KhultStudios"
                />
                <meta
                    name="twitter:description"
                    content="Professional product, catalog, eCommerce, advertising, and brand photography services in Hyderabad."
                />
                <meta
                    name="twitter:image"
                    content="https://khultstudios.com/images/product-brand-photography-banner.jpg"
                />

                {/* Favicon */}
                <link rel="icon" href="https://khultstudios.com/favicon.ico" />
            </Helmet>
            {/* FULLSCREEN VIDEO VIEWPORT BACKGROUND ENGINE */}
            <div className="video-bg-viewport">
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="global-html5-video"
                >
                    <source src="videos/khultstudio.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
                <div className="vignette-overlay-layer" />
            </div>

            {/* INNER APP CANVAS CONTAINER SECTION */}
            <div className="page-frame px-2 px-md-5 pb-5">
                <div className="landing-container position-relative overflow-hidden">
                    <div className="paper-texture" />
                    <div className="container-fluid h-100 p-4 p-lg-5 position-relative z-2">
                        <div className="row h-100 gy-5">

                            {/* Content Brief side description */}
                            <div className="col-lg-5 d-flex flex-column justify-content-center h-100 content-side">
                                <div className="invitation-text position-relative py-4">
                                    <h5 className="text-uppercase tracking-wider text-orange fw-bold mb-2">
                                        Products & Brands Photography Services
                                    </h5>
                                    <h1 className="display-3 text-uppercase main-title text-white mb-4">
                                        Transforming Objects into Premium Brand Statements
                                    </h1>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        A product is more than just an item on a shelf—it’s a physical embodiment of your brand’s identity. At KhultStudio, we specialize in high-impact commercial product photography and cohesive brand visual production that transforms casual browsers into loyal brand advocates.
                                    </p>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        Using high-resolution macro glass, state-of-the-art strobe lighting systems, and color-accurate workflows, we build dynamic digital assets designed to convert on global e-commerce systems, catalogs, and commercial campaigns.
                                    </p>
                                    <a
                                        href="#details"
                                        className="btn btn-orange text-uppercase px-4 py-2 text-dark fw-bold tracking-wide btn-scroll"
                                    >
                                        Explore Assets
                                    </a>
                                </div>
                                <div className="social-footer d-flex gap-3 mt-2">
                                    <a href="#" className="social-icon"><i className="fab fa-facebook-f" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-instagram" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-linkedin-in" /></a>
                                </div>
                            </div>

                            {/* Infinite Slanted Ribbon Gallery Strip components */}
                            <div className="col-lg-7 position-relative photo-zone-height">
                                <div className="filmstrip-skew-box">
                                    <div
                                        className="vertical-scroll-viewport"
                                        ref={viewportRef}
                                    >
                                        <div className="row gx-4 h-100">
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon" ref={leftStripRef} style={{ display: 'none' }}>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1523275335684-37898b6baf30?q=80&w=500" alt="Premium Watch Product Session" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1542291026-7eec264c27ff?q=80&w=500" alt="Minimalist Sneaker E-Commerce Still" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1608248597481-496100c8c836?q=80&w=500" alt="Cosmetic Branding Staging" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1560343090-f0409e92791a?q=80&w=500" alt="Luxury Leather Shoe Flat Lay" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1523275335684-37898b6baf30?q=80&w=500" alt="Premium Watch Product Session Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1542291026-7eec264c27ff?q=80&w=500" alt="Minimalist Sneaker E-Commerce Still Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1608248597481-496100c8c836?q=80&w=500" alt="Cosmetic Branding Staging Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1560343090-f0409e92791a?q=80&w=500" alt="Luxury Leather Shoe Flat Lay Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon ribbon-offset" ref={rightStripRef}>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/pre-wedding-and-wedding-photography.jpg" alt="Wedding 5" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/events-and-celebrations-photography.jpg" alt="Wedding 6" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/Arts.jpg" alt="Wedding 7" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/modelling-and-acting-photography.jpg" alt="Wedding 8" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="/home/movie-posters.jpg" alt="Wedding 5 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/pre-wedding-and-wedding-photography.jpg" alt="Wedding 6 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/products-and-brands-photography.jpg" alt="Wedding 7 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/home/maternity-and-baby-photography1.jpg" alt="Wedding 8 Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {/* PRODUCT PHOTOGRAPHY SERVICES SECTION */}
            <section className="services-section py-5" id="details">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">📦 High-Impact Commercial Product Media</h2>
                    <p className="text-light-gray mt-3 text-center max-w-2xl mx-auto">
                        In digital marketplaces, your product image *is* your product. From sharp, ultra-clean white background e-commerce layouts to complex lifestyle arrangements, we frame consumer goods to display their elite values clearly.
                    </p>
                    <div className="row g-4 mt-2">
                        {productServices.map((service, index) => (
                            <div key={index} className="col-md-4">
                                <div className={`service-card ${index === 1 ? 'active' : ''}`}>
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h3>{service.title}</h3>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* BRANDS PHOTOGRAPHY SERVICES SECTION */}
            <section className="wedding-services-section py-5">
                <div className="container">
                    <div className="text-center mb-5">
                        <h2 className="text-white">🏷️ Cohesive Brand Identity Visual Ecosystems</h2>
                        <p className="text-light-gray mt-3 max-w-2xl mx-auto">
                            Great brands don’t just sell items; they communicate core principles. Our brand production matrix captures corporate identity, executive leadership presence, and commercial lifestyle visuals to unite your digital footprint.
                        </p>
                    </div>

                    <div className="row g-4 justify-content-center">
                        {brandServices.map((service, index) => (
                            <div key={index} className="col-md-4">
                                <div className="service-card">
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h4>{service.title}</h4>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* HIGH-VOLUME COMPREHENSIVE BRAND DELIVERABLES MATRIX */}
            <section className="comprehensive-section py-5 bg-black-opacity-50">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">💼 Institutional Production Deliverables</h2>
                    <div className="row g-4">
                        {comprehensiveBrandOfferings.map((service, index) => (
                            <div key={index} className="col-lg-4 col-md-6">
                                <div className="service-card">
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h4>{service.title}</h4>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ProductsandBrands
